import { ApplicableList, ListModel, Visibility } from "lib/models/list-models";
import { CSRFService } from "lib/services/csrf-service";
import { ListsService } from "lib/services/lists-service";
import React, { createContext, ReactNode, useContext, useMemo, useReducer } from "react";

import * as actions from "./actions";
import { ListType } from "./constants";
import { initialState, listsReducer, ListsState } from "./reducer";
import * as selectors from "./selectors";

interface ListsContextType {
  state: ListsState;
  getMyLists: (options?: {
    includeItemDetails?: boolean;
    visibility?: Visibility;
    tags?: string[];
    allowedDomains?: string[];
    hasRules?: boolean;
  }) => Promise<void>;

  getPublicLists: (options?: {
    includeItemDetails?: boolean;
    tags?: string[];
    allowedDomains?: string[];
    hasRules?: boolean;
  }) => Promise<void>;

  getSharedLists: (options?: {
    includeItemDetails?: boolean;
    tags?: string[];
    allowedDomains?: string[];
    hasRules?: boolean;
  }) => Promise<void>;

  getSharedWithMeLists: (options?: {
    includeItemDetails?: boolean;
    tags?: string[];
    allowedDomains?: string[];
    hasRules?: boolean;
  }) => Promise<void>;

  evaluateLists: (
    attributes: Record<string, any>,
    options?: { domains?: string[]; includeItemDetails?: boolean; limit?: number }
  ) => Promise<ApplicableList[]>;

  createList: (
    data: {
      name: string;
      description?: string;
      visibility?: Visibility;
      allowedDomains?: string[];
      itemIds?: string[];
      evaluationRules?: DocumentType;
      tags?: string[];
    },
    listType: ListType
  ) => Promise<ListModel>;

  updateList: (
    listId: string,
    data: {
      name: string;
      description?: string;
      visibility?: Visibility;
      allowedDomains?: string[];
      evaluationRules?: DocumentType;
    },
    listType: ListType
  ) => Promise<ListModel>;

  deleteList: (listId: string, listType: ListType) => Promise<string>;

  addItemToList: (
    taxonomyIds: string[],
    options: { listId: string } | { autoCreate: true; name: string; domain: string },
    listType: ListType
  ) => Promise<ListModel>;
  removeItemFromList: (
    listId: string,
    itemId: string,
    listType: ListType
  ) => Promise<{ listId: string; itemId: string }>;
}

const ListsContext = createContext<ListsContextType | null>(null);

export const ListsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(listsReducer, initialState);
  const listsService = useMemo(() => new ListsService(), []);
  const csrfService = useMemo(() => new CSRFService(), []);

  const contextValue = useMemo(
    () => ({
      state,
      getMyLists: actions.getMyLists(dispatch, listsService),
      getPublicLists: actions.getPublicLists(dispatch, listsService),
      getSharedLists: actions.getSharedLists(dispatch, listsService),
      getSharedWithMeLists: actions.getSharedWithMeLists(dispatch, listsService),
      evaluateLists: actions.evaluateLists(listsService, csrfService),
      createList: actions.createList(dispatch, listsService, csrfService),
      updateList: actions.updateList(dispatch, listsService, csrfService),
      deleteList: actions.deleteList(dispatch, listsService, csrfService),
      addItemToList: actions.addItemToList(dispatch, listsService, csrfService),
      removeItemFromList: actions.removeItemFromList(dispatch, listsService, csrfService),
    }),
    [state, dispatch, listsService, csrfService]
  );

  return <ListsContext.Provider value={contextValue}>{children}</ListsContext.Provider>;
};

export const useLists = () => {
  const context = useContext(ListsContext);
  if (!context) {
    throw new Error("useLists must be used within a ListsProvider");
  }
  return {
    loading: selectors.selectLoading(context.state),
    error: selectors.selectError(context.state),
    creatingFavoriteList: selectors.selectCreatingFavoriteList(context.state),
    myLists: selectors.selectMyLists(context.state),
    publicLists: selectors.selectPublicLists(context.state),
    sharedLists: selectors.selectSharedLists(context.state),
    sharedWithMeLists: selectors.selectSharedWithMeLists(context.state),
    ...context,
  };
};
